import { useEffect, useState, type FormEvent } from "react";
import type { Me, PetSummary } from "../../shared/types";
import { api } from "../api";
import { navigate } from "../router";
import { fmtDate, speciesEmoji } from "../format";

export function PatientList({ me }: { me: Me }) {
  const [pets, setPets] = useState<PetSummary[] | null>(null);
  const [q, setQ] = useState("");
  const [chip, setChip] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      api.pets(q).then(setPets, (e) => setError(e.message));
    }, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [q]);

  async function scan(e: FormEvent, emergency = false) {
    e.preventDefault();
    const digits = chip.replace(/\D/g, "");
    if (digits.length < 9) return setError("Enter the full microchip number (9–15 digits).");
    setError(null);
    if (emergency) return navigate(`/emergency/${digits}`);
    setScanning(true);
    try {
      const { id } = await api.byChip(digits);
      navigate(`/pets/${id}`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setScanning(false);
    }
  }

  return (
    <main className="container">
      {me.role === "vet" && (
        <div className="scan">
          <div style={{ fontSize: 28.5 }}>⌁</div>
          <div>
            <div className="scan-t">Scan microchip</div>
            <div className="scan-s">One scan, full story — the verified record follows the pet between clinics.</div>
          </div>
          <button className="btn teal scan-mobile" onClick={() => navigate("/scan")}>📱 Scan chip — QR / RFID</button>
          <form onSubmit={(e) => scan(e)}>
            <input value={chip} onChange={(e) => setChip(e.target.value)} placeholder="941000029159723" inputMode="numeric" aria-label="Microchip number" />
            <button className="btn teal" disabled={scanning}>Open record</button>
            <button type="button" className="btn danger" onClick={(e) => scan(e, true)} title="Critical information only">Emergency</button>
          </form>
        </div>
      )}
      {error && <div className="err mb">{error}</div>}

      <input className="search" value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={me.role === "vet" ? "Search by pet name, owner, patient ID or microchip…" : "Search your pets…"} />

      <div className="sec">{me.role === "vet" ? "Patients on the network" : "My pets"}</div>
      {!pets ? (
        <div className="center" style={{ minHeight: 160 }}><div className="spinner" /></div>
      ) : pets.length === 0 ? (
        <div className="card" style={{ padding: 20, fontSize: 13, color: "var(--txt2)" }}>No pets found.</div>
      ) : (
        <div className="plist">
          {pets.map((p) => (
            <button key={p.id} className="pcard" onClick={() => navigate(`/pets/${p.id}`)}>
              {p.photoUrl ? <img className="avatar" src={p.photoUrl} alt="" /> : <div className="avatar">{speciesEmoji(p.species)}</div>}
              <div style={{ minWidth: 0 }}>
                <div className="n">{p.displayName}</div>
                <div className="s">{p.species} · {p.breed ?? "—"}{me.role === "vet" && p.ownerName ? ` · ${p.ownerName}` : ""}</div>
                <div className="c">Chip {p.microchip} · ID {p.patientNo}</div>
              </div>
              <div className="meta">
                {p.hasDrugAllergy && <span className="pill" style={{ background: "var(--hl)", color: "var(--hi)" }}>⛔ Drug allergy</span>}
                <span className="t3">{p.lastVisit ? `Last visit ${fmtDate(p.lastVisit)}` : "No visits"}</span>
                <span className="chain-badge">🔗 {p.blocks} blocks</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </main>
  );
}
